/* eslint-disable linebreak-style */

import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  FileExcelOutlined,
  ShareAltOutlined,
  DeleteOutlined,
  EditOutlined,
  UnlockOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
} from '@ant-design/icons';
import { Form, Input, notification, Tooltip, Typography, Upload, Table } from 'antd';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import { get } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Users, Plus, FileSpreadsheet } from 'lucide-react';
import { ConfirmationModal, Spinner, Button as CrcButton } from '@crc-evans/ui-components';

import AddUserModal from '../../components/AddUserModal';
import {
  getUsers,
  getAccess,
  getAllUsersList,
} from '../../services/teamMembers';
import {
  saveUsers,
  removeAccess,
  resetUserPassword,
  addAccess,
  removeUser,
} from '../../services/userManagement';
import ResetPasswordModal from '../../components/ResetPasswordModal';
import { useHeatStyleColumnFacets } from '../../components/technical/heatStyleColumnFacets';
import '../technical/HeatProfilePage.css';

const { Title, Text } = Typography;

const USER_MGMT_FACET_KEYS = ['name', 'email', 'userStatus'];

/**
 * getAllUsersGroupsList() may return { users: { [userId]: string[] } } or a top-level
 * { [userId]: string[] } map. Optional chaining on userGroupsList?.users[userId] is unsafe
 * when .users is missing (undefined[userId] throws).
 */
const normalizeGroupToken = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const toGroupTokens = (groups) => {
  const values = Array.isArray(groups) ? groups : [];
  const rawTokens = values
    .map((group) => {
      if (group == null) return '';
      if (typeof group === 'string') return group;
      if (typeof group === 'object') {
        return group.GroupName || group.groupName || group.name || group.key || group.value || '';
      }
      return '';
    })
    .filter(Boolean);

  return new Set(rawTokens.map(normalizeGroupToken).filter(Boolean));
};

/** Flatten access tree to Excel columns (header = immediate parent title + leaf title). */
const flattenExportTreeColumns = (nodes, parentTitle) => {
  const out = [];
  (nodes || []).forEach((node) => {
    if (node.children && node.children.length > 0) {
      out.push(...flattenExportTreeColumns(node.children, node.title));
    } else {
      const header = parentTitle ? `${parentTitle} - ${node.title}` : node.title;
      out.push({ header, key: node.key, width: 40 });
    }
  });
  return out;
};

/** Same leaf keys as Add User modal / Cognito groups. */
const USER_EXPORT_ACCESS_TREE = [
  { title: 'Super Admin', key: 'super-admin' },
  { title: 'Admin', key: 'admin' },
  {
    title: 'Dashboard',
    key: 'dashboard',
    children: [
      { title: 'Normal User', key: 'web-page-without-create-project' },
      { title: 'Normal User With Create Project', key: 'web-page' },
    ],
  },
  { title: 'Analysis', key: 'analysis' },
  {
    title: 'Ask Evans',
    key: 'AskEvans',
    children: [
      { title: 'Admin', key: 'askevans-training-data' },
      { title: 'Normal User', key: 'askevans-normal-user' },
    ],
  },
  {
    title: 'Impact Dashboard',
    key: 'Impact-Dashboard',
    children: [
      { title: 'Admin', key: 'impact-dashboard' },
      { title: 'Senior Leadership Team', key: 'impact-dashboard-slt' },
      { title: 'Normal User', key: 'impact-dashboard-normal-user' },
    ],
  },
  {
    title: 'Technical',
    key: 'technical',
    children: [
      { title: 'Heat Profile', key: 'heat-profile' },
      { title: 'Coating Record Database', key: 'coating-record-db' },
      {
        title: 'DCAL Generator',
        key: 'dcal-generators',
        children: [
          { title: 'Admin', key: 'dcal-generator-master' },
          { title: 'Normal User', key: 'dcal-generator' },
        ],
      },
    ],
  },
  {
    title: 'Sales',
    key: 'sales',
    children: [
      {
        title: 'Cycle Time',
        key: 'cycle-time',
        children: [
          { title: 'Admin', key: 'ct-admin' },
          { title: 'Normal User', key: 'ct-normal-user' },
        ],
      },
    ],
  },
  {
    title: 'Engineering',
    key: 'engineering',
    children: [
      { title: 'Coil Configurator', key: 'coil_configurator' },
      { title: 'On Pipe Configurator', key: 'on_pipe_configurator' },
    ],
  },
  {
    title: 'Calibration & Control',
    key: 'Calibration-Control',
    children: [{ title: 'Calibration', key: 'calibration-normal-user' }],
  },
  {
    title: 'Lesson Learned',
    key: 'lesson-learned',
    children: [
      { title: 'Admin', key: 'LessonLearnedAdmin' },
      { title: 'Approver', key: 'LessonLearnedApprover' },
      { title: 'Normal User', key: 'LessonLearnedNormalUser' },
    ],
  },
  {
    title: 'Downtime Tracker',
    key: 'downtime-tracker',
    children: [
      { title: 'Admin', key: 'downtime-tracker-admin' },
      { title: 'Normal User', key: 'downtime-tracker-normal-user' },
      { title: 'Viewer', key: 'downtime-tracker-viewer' },
    ],
  },
];

const USER_EXPORT_TREE_COLUMNS = flattenExportTreeColumns(USER_EXPORT_ACCESS_TREE);
const USER_EXPORT_COLUMN_GROUP_TOKENS = USER_EXPORT_TREE_COLUMNS.map((c) => normalizeGroupToken(c.key));

/** Parallel map with bounded concurrency — faster than sequential chunk awaits. */
async function mapWithConcurrency(items, limit, mapper) {
  if (!items.length) return [];
  const n = Math.min(Math.max(1, limit), items.length);
  const result = new Array(items.length);
  let nextIndex = 0;
  const worker = async () => {
    for (;;) {
      const i = nextIndex++;
      if (i >= items.length) break;
      result[i] = await mapper(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: n }, () => worker()));
  return result;
}

const GET_ACCESS_EXPORT_CONCURRENCY = 14;

const normalizeAccessGroups = (response) => {
  if (Array.isArray(response)) {
    return response
      .map((item) => item?.GroupName || item?.groupName || item?.name || item)
      .filter(Boolean);
  }
  if (!response || typeof response !== 'object') return [];
  if (Array.isArray(response.groups)) return response.groups;
  if (Array.isArray(response.groupNames)) return response.groupNames;
  if (Array.isArray(response.data)) return normalizeAccessGroups(response.data);
  return [];
};

const UserManagementAGGrid = (props) => {
  const { } = props;

  const [columnFilters, setColumnFilters] = useState({
    name: '',
    email: '',
    userStatus: '',
  });

  const [filteredData, setFilteredData] = useState([]);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [userModalLoading, setUserModalLoading] = useState(false);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [resetPasswordModalLoading, setResetPasswordModalLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [userData, setUserData] = useState([]);
  const [showLoader, setShowLoader] = useState(true);
  const [teamModalLoading, setTeamModalLoading] = useState(false);
  const [createUserLoader, setCreateUserLoader] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [userForm] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageTokens, setPageTokens] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [userName, setUserName] = useState('');
  const [userGroup, setUserGroup] = useState([]);
  const [resetPasswordRecord, setResetPasswordRecord] = useState([]);
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  const [sortKey, setSortKey] = useState('name');
  const [sortDesc, setSortDesc] = useState(false);

  const getUsersList = async (pageToken, pageAction, searchKeyExist) => {
    setShowLoader(true);
    const response = await getAllUsersList();
    if (response && response.users.length) {
      setPageTokens([...pageTokens, response.paginationToken]);
      setUserCount(response.count);
      // eslint-disable-next-line consistent-return
      let usersForProject = response.users.map((obj) => {
        const details = { user_id: obj.Username };
        obj.Attributes.forEach((element) => {
          if (element.Name === 'custom:first_name') {
            details.userFirstName = element.Value;
          } else if (element.Name === 'custom:last_name') {
            details.userLastName = element.Value;
          } else if (element.Name === 'name') {
            details.cognitoName = element.Value;
          } else if (element.Name === 'email') {
            details.email = element.Value;
          }
        });
        const first = (details.userFirstName || '').trim();
        const last = (details.userLastName || '').trim();
        const customName = first ? (last ? `${first} ${last}` : first) : '';
        details.name =
          customName || (details.cognitoName || '').trim() || (details.email || '').trim();
        details.userStatus = obj.UserStatus;
        details.enabled = obj.Enabled;
        if (details.name && details.email && details.userStatus && details.enabled) {
          return details;
        }
      });
      usersForProject = usersForProject.filter((el) => el);
      usersForProject.sort((a, b) => a.name.localeCompare(b.name));
      setUserData(usersForProject);
      setShowLoader(false);
    } else if (response.users.length == 0) {
      // notification.warning({
      //   message: 'User does not exist with the given email.',
      //   duration: 1.2
      // });
      console.log('User does not exist with the given email');
      setUserData(null);
      setShowLoader(false);
    } else {
      notification.error({
        message: 'Something went wrong.',
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await getUsersList();
    };

    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleColumnFilterChange = useCallback((key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const userListFacetRef = useRef([]);
  userListFacetRef.current = Array.isArray(userData) ? userData : [];

  const fetchUserFacetRows = useCallback(async () => {
    const list = userListFacetRef.current;
    return Array.isArray(list) ? [...list] : [];
  }, []);

  const searchCriteriaForFacets = useMemo(() => ({}), []);

  const { columnSelectedValues, applyFacetClientFilter, renderTextColumnFilterTrigger } =
    useHeatStyleColumnFacets({
      enabled: true,
      filterableColumns: USER_MGMT_FACET_KEYS,
      listCount: Array.isArray(userData) ? userData.length : 0,
      projectList: Array.isArray(userData) ? userData : [],
      searchCriteria: searchCriteriaForFacets,
      columnFilters,
      onColumnFilterChange: handleColumnFilterChange,
      fetchFacetRows: fetchUserFacetRows,
    });

  // Filtering Logic (header text + Heat-style column facets)
  useEffect(() => {
    let result = Array.isArray(userData) ? [...userData] : [];
    for (const key of USER_MGMT_FACET_KEYS) {
      const sel = columnSelectedValues[key];
      if (sel != null && sel.size > 0) continue;
      const val = (columnFilters[key] || '').trim();
      if (!val) continue;
      const q = val.toLowerCase();
      result = result.filter((item) => {
        const cell = item[key];
        const str = cell != null ? String(cell) : '';
        return str.toLowerCase().includes(q);
      });
    }
    setFilteredData(applyFacetClientFilter(result));
  }, [userData, columnFilters, columnSelectedValues, applyFacetClientFilter]);

  const handleSortClick = useCallback((field) => {
    setSortKey((prev) => {
      if (prev === field) {
        setSortDesc((d) => !d);
        return prev;
      }
      setSortDesc(false);
      return field;
    });
  }, []);

  const renderSortCaret = (field) =>
    sortKey === field ? (
      sortDesc ? (
        <CaretDownOutlined className="equipment-type-sort-dir-icon" aria-hidden />
      ) : (
        <CaretUpOutlined className="equipment-type-sort-dir-icon" aria-hidden />
      )
    ) : (
      <CaretUpOutlined
        className="equipment-type-sort-dir-icon doc-control-sort-caret-inactive"
        aria-hidden
      />
    );

  const renderUserColumnTitle = (title, dataIndex) => (
    <div className="column-header-with-filter">
      <div className="column-header-text-wrapper spares-column-header-sortable">
        <span>{title}</span>
        <span
          className="custom-sort-icon"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleSortClick(dataIndex);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSortClick(dataIndex);
            }
          }}
        >
          {renderSortCaret(dataIndex)}
        </span>
        {renderTextColumnFilterTrigger(dataIndex)}
      </div>
      <div className="header-filter-input user-mgmt-header-search">
        <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm heat-profile-header-inline-shell">
          <Input
            placeholder="Search"
            value={columnFilters[dataIndex] || ''}
            onChange={(e) => handleColumnFilterChange(dataIndex, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            size="small"
            style={{ width: '100%' }}
          />
        </div>
      </div>
    </div>
  );

  const sortedFilteredData = useMemo(() => {
    const list = [...(filteredData || [])];
    list.sort((a, b) => {
      const va = (a?.[sortKey] ?? '').toString();
      const vb = (b?.[sortKey] ?? '').toString();
      const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
      return sortDesc ? -cmp : cmp;
    });
    return list;
  }, [filteredData, sortDesc, sortKey]);

  const showResetPasswordModal = (record) => {
    setResetPasswordRecord(record);
    setResetPasswordModalVisible(true);
  };

  const openDeleteUserModal = (record) => {
    setUserToDelete(record);
    setDeleteUserModalOpen(true);
  };

  const closeDeleteUserModal = () => {
    if (deleteUserLoading) return;
    setDeleteUserModalOpen(false);
    setUserToDelete(null);
  };

  const handleConfirmDeleteUser = () => {
    if (!userToDelete || deleteUserLoading) return;
    setDeleteUserLoading(true);
    removeUser(userToDelete.user_id, userToDelete.email)
      .then((res) => {
        if (res) {
          notification.success({
            message: 'User deleted successfully',
          });
          getUsersList();
          setDeleteUserModalOpen(false);
          setUserToDelete(null);
        } else {
          notification.error({
            message: 'Something went wrong.',
          });
        }
      })
      .catch(() =>
        notification.error({
          message: 'Something went wrong.',
        })
      )
      .finally(() => setDeleteUserLoading(false));
  };

  const saveUser = async (data, sendWelcomeEmail = true) => {
    const res = await saveUsers(data);

    // Check for success response: { "message": "success", "data": { "User": {...} } }
    if (res.status === 200 && (res.message === 'success' || res.data?.User) && !res.error) {
      const userData = res.data?.User;
      const userStatus = userData?.UserStatus;

      // Check if user already exists (e.g., FORCE_CHANGE_PASSWORD status)
      const userExists = userStatus === 'FORCE_CHANGE_PASSWORD' || userStatus === 'CONFIRMED';

      if (userExists && !sendWelcomeEmail) {
        // User exists but we're not sending email - treat as success
        notification.success({
          message: 'User already exists.',
          description: `${data.email} is already in the system.`,
          duration: 3,
        });
      } else if (sendWelcomeEmail) {
        notification.success({
          message: 'User created successfully!',
          description: `An invitation email has been sent to ${data.email} with a link to set their password.`,
          duration: 4,
        });
      } else {
        notification.success({
          message: 'User created successfully!',
          description: `${data.email} has been added. No invitation email was sent.`,
          duration: 3,
        });
      }
      userForm.resetFields();
      setUserModalVisible(false);
      getUsersList();
    } else {
      const errorMessage =
        res?.error || 'Could not create this user. Please check the details and try again.';
      userForm.setFields([{ name: 'email', errors: [errorMessage] }]);
      notification.error({
        message: 'Unable to create user',
        description: errorMessage,
        duration: 5,
      });
    }
    setCreateUserLoader(false);
    setUserModalLoading(false);
    return res;
  };

  const resetPassword = async (data) => {
    resetPasswordForm.resetFields();
    const response = await resetUserPassword(data);
    if (response) {
      notification.success({
        message: 'Password has been reset successfully',
      });
    } else {
      notification.success({
        message: 'Something went wrong',
      });
    }
    setResetPasswordModalLoading(false);
    setResetPasswordModalVisible(false);
  };

  const showAddEditUserModal = async (record, edit) => {
    console.log(record);
    let userDt = {};
    if (record !== '') {
      const response = await getAccess(record.user_id);
      const userAccess = response.map((item) => item?.GroupName);
      const fullName = record.name.split(' ');
      const fName = fullName[0];
      if (fullName.length === 2) {
        const lName = fullName[1];
        userDt = {
          email: record.email,
          firstName: fName,
          lastName: lName,
          access: userAccess,
        };
      } else {
        userDt = {
          email: record.email,
          firstName: fName,
          access: userAccess,
        };
      }
      userForm.setFieldsValue(userDt);
      setUserName(record.user_id);
      setUserGroup(userAccess);
    } else {
      userForm.resetFields();
    }

    setIsEdit(edit);

    setUserModalVisible(true);
  };

  const onAdduserToGroup = async (dataList) => {
    try {
      setUserModalLoading(true);
      const failedGroups = [];

      // Loop through all data and process them
      for (const data of dataList) {
        const response = await addAccess(data);
        if (response !== 200) {
          failedGroups.push(data); // Store failed attempts
        }
      }

      // Show notification based on success or failure
      if (failedGroups.length > 0) {
        notification.error({
          message: `Failed to add ${failedGroups.length} group(s).`,
          duration: 1.5,
        });
      } else {
        notification.success({
          message: 'User added to groups successfully',
          duration: 1.5,
        });
      }

      await getUsersList(); // Refresh user list
    } catch (error) {
      notification.error({
        message: 'Something went wrong.',
      });
    } finally {
      setUserModalLoading(false);
      setCreateUserLoader(false);
      setUserModalVisible(false);
    }
  };

  const onRemoveuserFromGroup = async (dataList) => {
    try {
      setUserModalLoading(true);
      const failedGroups = [];

      // Loop through all data and process them
      for (const data of dataList) {
        const response = await removeAccess(data);
        if (response !== 200) {
          failedGroups.push(data); // Store failed attempts
        }
      }

      // Show notification based on success or failure
      if (failedGroups.length > 0) {
        notification.error({
          message: `Failed to remove ${failedGroups.length} group(s).`,
          duration: 1.5,
        });
      } else {
        notification.success({
          message: 'User removed from groups successfully',
          duration: 1.5,
        });
      }

      await getUsersList(); // Refresh user list
    } catch (error) {
      notification.error({
        message: 'Something went wrong.',
      });
    } finally {
      setUserModalLoading(false);
      setCreateUserLoader(false);
      setUserModalVisible(false);
    }
  };

  const columns = [
    {
      title: (
        <div className="column-header-with-filter user-mgmt-serial-column-header">
          <div className="column-header-text-wrapper">
            <span>S.No</span>
          </div>
          <div className="header-filter-input" aria-hidden="true">
            <div className="user-mgmt-serial-header-spacer" />
          </div>
        </div>
      ),
      key: 'serial',
      width: 72,
      align: 'center',
      fixed: 'left',
      onHeaderCell: () => ({ className: 'user-mgmt-serial-cell' }),
      onCell: () => ({ className: 'user-mgmt-serial-cell' }),
      render: (_text, _record, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: renderUserColumnTitle('Name', 'name'),
      dataIndex: 'name',
      key: 'name',
      width: '250px',
    },
    {
      title: renderUserColumnTitle('Email', 'email'),
      dataIndex: 'email',
      key: 'email',
      width: '350px',
    },
    {
      title: renderUserColumnTitle('Confirm Status', 'userStatus'),
      dataIndex: 'userStatus',
      key: 'userStatus',
      width: '280px',
      align: 'left',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '150px',
      align: 'center',
      fixed: 'right',
      render: (text, record) => (
        <div className="heat-profile-actions-cell" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <Tooltip title="Edit User Access">
            <EditOutlined
              className="txtIcon"
              style={{ color: '#00A86B', fontSize: '18px', cursor: 'pointer' }}
              onClick={() => showAddEditUserModal(record, true)}
            />
          </Tooltip>
          <Tooltip title="Reset User Password">
            <UnlockOutlined
              className="txtIcon"
              style={{ color: '#1677ff', fontSize: '18px', cursor: 'pointer' }}
              onClick={() => showResetPasswordModal(record, true)}
            />
          </Tooltip>
          <Tooltip title="Delete User">
            <DeleteOutlined
              className="txtIcon"
              style={{ color: '#ef314c', fontSize: '18px', cursor: 'pointer' }}
              onClick={() => openDeleteUserModal(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [sortKey, sortDesc, columnFilters, columnSelectedValues]);

  const handleDownloadData = async () => {
    setDownloadLoading(true);
    try {
      const treeColumns = USER_EXPORT_TREE_COLUMNS;
      const workbook = new ExcelJS.Workbook();
      const databaseList = sortedFilteredData;

      if (databaseList && databaseList.length > 0) {
        const sheet = workbook.addWorksheet('User List');
        const headerRow = sheet.getRow(1);
        headerRow.height = 20;

        const columns = [
          { header: 'S.No', key: 'sno', width: 10 },
          ...Object.keys({
            name: 'Name',
            email: 'Email',
            userStatus: 'Confirm Status',
          }).map((key) => ({
            header: {
              name: 'Name',
              email: 'Email',
              userStatus: 'Confirm Status',
            }[key],
            key,
            width: 40,
          })),
          ...treeColumns,
        ];

        sheet.columns = columns;

        const usersWithGroups = [...databaseList];
        const usersGroupsMap = {};

        const groupLists = await mapWithConcurrency(
          usersWithGroups,
          GET_ACCESS_EXPORT_CONCURRENCY,
          async (user) => {
            try {
              const value = await getAccess(user.user_id);
              return normalizeAccessGroups(value);
            } catch {
              return [];
            }
          },
        );
        usersWithGroups.forEach((user, i) => {
          usersGroupsMap[user.user_id] = groupLists[i] || [];
        });

        const filledData = usersWithGroups.map((user, rowIndex) => {
          const extendedRow = { sno: rowIndex + 1, ...user };
          const groupTokens = toGroupTokens(usersGroupsMap[user.user_id] || []);

          for (let i = 0; i < treeColumns.length; i++) {
            const col = treeColumns[i];
            const token = USER_EXPORT_COLUMN_GROUP_TOKENS[i];
            extendedRow[col.key] = token && groupTokens.has(token) ? 'Yes' : '';
          }

          return extendedRow;
        });

        sheet.addRows(filledData);
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `List of User ${moment().format('DD-MM-YYYY')}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <>
      <div className="heat-profile-content-wrapper">
        <div className="heat-profile-card">
          <div className="heat-profile-list-header">
            <div className="heat-profile-header-left">
              <div className="heat-profile-icon-wrap" style={{ backgroundColor: '#f3e8ff' }}>
                <Users size={24} color="#9333ea" />
              </div>
              <div className="heat-profile-header-content">
                <div className="heat-profile-title-row">
                  <h3 className="heat-profile-list-title">List of Users</h3>
                </div>
                <p className="heat-profile-list-description">
                  Manage user accounts, system access, and passwords.
                </p>
              </div>
            </div>

            <div className="heat-profile-header-actions calibration-modern-actions">
              <CrcButton
                label="Download As Excel"
                variant="tertiary"
                icon={FileSpreadsheet}
                onClick={handleDownloadData}
                loading={downloadLoading}
                disabled={downloadLoading || !(userData && userData.length > 0)}
                size="small"
              />
              <CrcButton
                label="Create New User"
                variant="primary"
                icon={Plus}
                onClick={() => showAddEditUserModal('', false)}
                size="small"
              />
            </div>
          </div>

          <div className="heat-profile-table-wrap" style={{ marginTop: '0px' }}>
            <Table
              className="heat-profile-table"
              columns={columns}
              dataSource={sortedFilteredData}
              loading={{
                spinning: showLoader,
                indicator: <Spinner variant="inline" size="large" color="#00C9A7" />,
              }}
              rowKey="user_id"
              scroll={{ x: 'max-content' }}
              pagination={{
                current: currentPage,
                pageSize,
                total: sortedFilteredData.length,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, newSize) => {
                  setCurrentPage(page);
                  setPageSize(newSize);
                },
              }}
            />
          </div>
        </div>
      </div>

      {userModalVisible ? (
        <AddUserModal
          setUserModalVisible={setUserModalVisible}
          setUserModalLoading={setUserModalLoading}
          userModalVisible={userModalVisible}
          userForm={userForm}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          onSave={saveUser}
          setCreateUserLoader={setCreateUserLoader}
          createUserLoader={createUserLoader}
          userGroup={userGroup}
          setUserGroup={setUserGroup}
          setUserName={setUserName}
          userName={userName}
          OnRemoveuserFromGroup={onRemoveuserFromGroup}
          OnAdduserToGroup={onAdduserToGroup}
        />
      ) : null}
      {resetPasswordModalVisible ? (
        <ResetPasswordModal
          setResetPasswordModalVisible={setResetPasswordModalVisible}
          resetPasswordModalVisible={resetPasswordModalVisible}
          setResetPasswordModalLoading={(value) => setResetPasswordModalLoading(value)}
          resetPasswordModalLoading={resetPasswordModalLoading}
          setResetPasswordRecord={setResetPasswordRecord}
          resetPasswordRecord={resetPasswordRecord}
        />
      ) : null}
      <ConfirmationModal
        className="user-mgmt-crc-delete-confirm"
        isOpen={deleteUserModalOpen}
        title="Remove this user?"
        message={
          userToDelete
            ? `This action will permanently remove ${userToDelete.name || userToDelete.email}. This cannot be undone.`
            : ''
        }
        confirmText={deleteUserLoading ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={handleConfirmDeleteUser}
        onCancel={closeDeleteUserModal}
        size="medium"
        confirmButtonProps={{
          disabled: deleteUserLoading,
          backgroundColor: '#00C9A7',
          textColor: '#FFFFFF',
        }}
        cancelButtonProps={{
          disabled: deleteUserLoading,
          backgroundColor: '#FFFFFF',
          textColor: '#374151',
        }}
      />
    </>
  );
};

export default connect(null, null)(UserManagementAGGrid);
